import { Edit, ExpandMore } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useState } from 'react';
import { PRICING_TYPES } from '../../../../utility/Constant';
import { HtmlTooltip } from '../../../../utility/helpers/commonFunction';
import { formatCurrency } from '../../../../utility/helpers/displayHelper';
import { formatVehicleMakeDisplay } from '../../../../utility/helpers/vehicleMakeHelper';
import VehicleMakesModal from '../SavedServiceList/VehicleMakesModal';

// Styles object matching VariantSelectionModal
const styles = {
    tableRow: {
        cursor: 'pointer',
        backgroundColor: '#fff',
        '&:hover': { backgroundColor: '#f9f9f9' },
    },
    collapseCell: {
        border: '1px solid #E6E6E6',
        borderRight: 'none',
        textAlign: 'center',
        cursor: 'pointer',
        padding: '10.5px 12px',
    },
    collapseCellExpanded: {
        border: '1px solid #E6E6E6',
        borderRight: 'none',
        borderBottom: 'none',
        textAlign: 'center',
        cursor: 'pointer',
        padding: '10.5px 12px',
    },
    collapseButton: {
        backgroundColor: '#E6E6E6',
        color: '#666',
        width: '24px',
        height: '24px',
        borderRadius: '8px',
        '&:hover': { backgroundColor: '#D0D0D0' },
    },
    collapseIcon: {
        color: '#000',
        fontSize: '20px',
        transition: 'transform 0.3s ease',
    },
    collapseIconExpanded: {
        color: '#000',
        fontSize: '20px',
        transform: 'rotate(180deg)',
        transition: 'transform 0.3s ease',
    },
    bodyCell: {
        padding: '8px 12px',
        border: '1px solid #E6E6E6',
        borderRight: 'none',
        borderLeft: 'none',
    },
    bodyCellExpanded: {
        padding: '8px 12px',
        border: '1px solid #E6E6E6',
        borderRight: 'none',
        borderLeft: 'none',
        borderBottom: 'none',
    },
    bodyCellLast: {
        padding: '8px 12px',
        border: '1px solid #E6E6E6',
        borderLeft: 'none',
    },
    bodyCellLastExpanded: {
        padding: '8px 12px',
        border: '1px solid #E6E6E6',
        borderLeft: 'none',
        borderBottom: 'none',
    },
    expandedCell: {
        padding: '8px 0',
        backgroundColor: '#EEF5FD',
        border: '1px solid #E6E6E6',
        borderTop: 'none',
    },
    expandedTableContainer: {
        maxHeight: '200px',
        overflow: 'auto',
        border: 'none',
        backgroundColor: '#EEF5FD',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
        },
    },
    expandedTable: {
        borderCollapse: 'separate',
        borderSpacing: 0,
        backgroundColor: '#EEF5FD',
        '& .MuiTableCell-root': {
            fontSize: '14px', // applies to all header + body cells
        },
    },
    expandedHeaderCell: {
        backgroundColor: '#EEF5FD',
        fontWeight: 500,
        fontSize: '14px',
        border: 'none',
        padding: '8px 16px',
    },
    expandedHeaderCellLeft: {
        backgroundColor: '#EEF5FD',
        fontWeight: 500,
        fontSize: '14px',
        border: 'none',
        padding: '8px 16px',
    },
    expandedBodyRow: {
        backgroundColor: '#EEF5FD',
    },
    expandedBodyCell: {
        fontSize: '14px',
        border: 'none',
        backgroundColor: '#EEF5FD',
        padding: '8px 16px',
    },
    expandedBodyCellLeft: {
        fontSize: '14px',
        border: 'none',
        backgroundColor: '#EEF5FD',
        padding: '8px 16px',
    },
};

const ServiceItem = ({ service, isExpanded, onExpandToggle, onServiceToggle, onEditService, serviceListPayload = [] }) => {
    const [vehicleMakesModalOpen, setVehicleMakesModalOpen] = useState(false);
    const [selectedVehicleMakes, setSelectedVehicleMakes] = useState([]);

    const partsCell = document.getElementById('parts-table-cell');
    const labourCell = document.getElementById('labour-table-cell');
    const amountCell = document.getElementById('amount-table-cell');

    const handleRowClick = () => {
        if (service.disabled) return;
        onServiceToggle(service.id);
    };

    // Process vehicle makes data similar to SavedServiceTable
    const processVehicleMakes = (vehicleMakeData) => {
        if (!vehicleMakeData) {
            return { vehicleMakes: [], vehicleMakeDisplay: 'All' };
        }

        let vehicleMakesArray = [];

        // Handle array format (new grouped makes)
        if (Array.isArray(vehicleMakeData)) {
            vehicleMakesArray = vehicleMakeData;
        }
        // Handle single object format (legacy)
        else if (typeof vehicleMakeData === 'object' && vehicleMakeData !== null) {
            vehicleMakesArray = [vehicleMakeData];
        }

        // Check if "all" is selected (empty array or object with _id/name === 'all')
        const isAllSelected = vehicleMakesArray.length === 0 || vehicleMakesArray.some((make) => make._id === 'all' || make.name === 'All');

        const displayText = isAllSelected ? 'All' : formatVehicleMakeDisplay(vehicleMakesArray);

        return {
            vehicleMakes: vehicleMakesArray,
            vehicleMakeDisplay: displayText,
            isAllSelected,
        };
    };

    const { vehicleMakes, vehicleMakeDisplay, isAllSelected } = processVehicleMakes(service?.vehicleMake);

    // Handle vehicle makes modal
    const handleVehicleMakesClick = () => {
        if (!isAllSelected && vehicleMakes.length > 1) {
            setSelectedVehicleMakes(vehicleMakes);
            setVehicleMakesModalOpen(true);
        }
    };

    const handleVehicleMakesModalClose = () => {
        setVehicleMakesModalOpen(false);
        setSelectedVehicleMakes([]);
    };

    return (
        <>
            {/* Main Service Row */}
            <TableRow sx={styles.tableRow} hover onClick={handleRowClick}>
                {/* Collapse Button Column */}
                <TableCell
                    sx={isExpanded ? styles.collapseCellExpanded : styles.collapseCell}
                    onClick={() => onExpandToggle(service.id)}
                    style={{ maxWidth: '56px' }}
                >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <IconButton
                            size="small"
                            sx={styles.collapseButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                onExpandToggle(service.id);
                            }}
                        >
                            <ExpandMore sx={isExpanded ? styles.collapseIconExpanded : styles.collapseIcon} />
                        </IconButton>
                        <HtmlTooltip title={service.disabled ? 'This service is already added to the invoice' : ''} placement="top" arrow>
                            <span onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={service.selected}
                                    disabled={service.disabled}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => onServiceToggle(service.id)}
                                    size="medium"
                                    sx={{
                                        padding: '4px',
                                        opacity: service.disabled ? 0.5 : 1,
                                        cursor: service.disabled ? 'not-allowed' : 'pointer',
                                    }}
                                />
                            </span>
                        </HtmlTooltip>
                    </Stack>
                </TableCell>

                {/* Service Name Column */}
                <TableCell sx={isExpanded ? styles.bodyCellExpanded : styles.bodyCell}>
                    <Typography variant="body2" fontWeight={400} fontSize={16}>
                        {service.name}
                    </Typography>
                </TableCell>

                {/* Shop Reference Column */}
                <TableCell sx={isExpanded ? styles.bodyCellExpanded : styles.bodyCell}>
                    <Typography variant="body2" fontSize={16}>
                        {service.shopReference || '-'}
                    </Typography>
                </TableCell>

                {/* Vehicle Make Column */}
                <TableCell sx={isExpanded ? styles.bodyCellExpanded : styles.bodyCell}>
                    <Typography
                        variant="body2"
                        fontWeight={400}
                        fontSize={16}
                        sx={{
                            cursor: !isAllSelected && vehicleMakes.length > 1 ? 'pointer' : 'default',
                            textDecoration: !isAllSelected && vehicleMakes.length > 1 ? 'underline' : 'none',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleVehicleMakesClick();
                        }}
                    >
                        {vehicleMakeDisplay}
                    </Typography>
                </TableCell>

                {/* Parts Column */}
                <TableCell sx={isExpanded ? styles.bodyCellExpanded : styles.bodyCell} align="center">
                    <Typography variant="body2" fontSize={16}>
                        {service.pricingType === PRICING_TYPES.LABOUR ? service.parts : ''}
                    </Typography>
                </TableCell>

                {/* Labour Column */}
                <TableCell sx={isExpanded ? styles.bodyCellExpanded : styles.bodyCell} align="center">
                    <Typography variant="body2" fontSize={16}>
                        {service.pricingType === PRICING_TYPES.LABOUR ? service.labour || 0 : ''}
                    </Typography>
                </TableCell>

                {/* Amount Column */}
                <TableCell align="left" sx={isExpanded ? styles.bodyCellLastExpanded : styles.bodyCellLast}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={400} fontSize={16}>
                            {formatCurrency(service.amount)}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditService(service.id);
                            }}
                            sx={{
                                color: 'rgba(0, 0, 0, 0.56)',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                },
                            }}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>

            {/* Expanded Details Row */}
            {isExpanded && (
                <TableRow style={{ position: 'relative', top: '-8px' }}>
                    <TableCell colSpan={8} sx={styles.expandedCell}>
                        <Box sx={styles.expandedTableContainer}>
                            <TableContainer>
                                <Table sx={styles.expandedTable}>
                                    {service.pricingType === 'fixed' ? (
                                        // Fixed pricing - show table header and description/amount in proper columns
                                        <>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="right" sx={styles.expandedHeaderCellLeft} width={82}>
                                                        No
                                                    </TableCell>
                                                    <TableCell sx={styles.expandedHeaderCellLeft}>Description</TableCell>
                                                    <TableCell align="center" sx={styles.expandedHeaderCell}>
                                                        Quantity
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        style={{ width: `${labourCell?.offsetWidth || 110}px` }}
                                                        sx={styles.expandedHeaderCell}
                                                    >
                                                        Cost
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        style={{ width: `${partsCell?.offsetWidth || 110}px` }}
                                                        sx={styles.expandedHeaderCell}
                                                    >
                                                        Price
                                                    </TableCell>
                                                    <TableCell
                                                        align="left"
                                                        style={{ width: `${amountCell?.offsetWidth || 110}px` }}
                                                        sx={styles.expandedHeaderCell}
                                                    >
                                                        Amount
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow sx={styles.expandedBodyRow}>
                                                    <TableCell align="right" sx={styles.expandedBodyCellLeft}>
                                                        <Typography variant="body2" mr={'8px'}>
                                                            1
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={styles.expandedBodyCellLeft}>
                                                        <Typography variant="body2">{service.description || service.name}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center" sx={styles.expandedBodyCell}>
                                                        <Typography variant="body2">-</Typography>
                                                    </TableCell>
                                                    <TableCell align="center" sx={styles.expandedBodyCell}>
                                                        <Typography variant="body2">-</Typography>
                                                    </TableCell>
                                                    <TableCell align="center" sx={styles.expandedBodyCell}>
                                                        <Typography variant="body2">-</Typography>
                                                    </TableCell>
                                                    <TableCell align="left" sx={styles.expandedBodyCell}>
                                                        <Typography variant="body2" fontWeight={400}>
                                                            {formatCurrency(service.fixedPrice || service.amount)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </>
                                    ) : (
                                        // Labour pricing or parts - show detailed breakdown
                                        <>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="right" width={82} sx={styles.expandedHeaderCellLeft}>
                                                        No
                                                    </TableCell>
                                                    <TableCell sx={styles.expandedHeaderCellLeft}>Description</TableCell>
                                                    <TableCell align="center" style={{ width: '110px' }} sx={styles.expandedHeaderCell}>
                                                        Quantity
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        style={{ width: `${partsCell?.offsetWidth || 110}px` }}
                                                        sx={styles.expandedHeaderCell}
                                                    >
                                                        Cost
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        style={{ width: `${labourCell?.offsetWidth || 110}px` }}
                                                        sx={styles.expandedHeaderCell}
                                                    >
                                                        Price
                                                    </TableCell>
                                                    <TableCell
                                                        align="left"
                                                        style={{ width: `${amountCell?.offsetWidth || 110}px` }}
                                                        sx={styles.expandedHeaderCell}
                                                    >
                                                        Amount
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {service.description && (
                                                    <TableRow sx={styles.expandedBodyRow}>
                                                        <TableCell align="right" sx={styles.expandedBodyCellLeft} />
                                                        <TableCell sx={styles.expandedBodyCellLeft}>
                                                            <Typography variant="body2">{service.description}</Typography>
                                                        </TableCell>
                                                        <TableCell sx={styles.expandedBodyCell} />
                                                        <TableCell sx={styles.expandedBodyCell} />
                                                        <TableCell sx={styles.expandedBodyCell} />
                                                        <TableCell sx={styles.expandedBodyCell} />
                                                    </TableRow>
                                                )}
                                                {/* Show labour row if labour pricing type and labour exists */}
                                                {service.pricingType === PRICING_TYPES.LABOUR && (service.labour > 0 || service.laborCost > 0) && (
                                                    <TableRow sx={styles.expandedBodyRow}>
                                                        <TableCell align="right" sx={styles.expandedBodyCellLeft}>
                                                            <Typography variant="body2" mr={'8px'}>
                                                                1
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={styles.expandedBodyCellLeft}>
                                                            <Typography variant="body2">Labour</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">{service.labour}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">-</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">{formatCurrency(service.laborCost || 0)}</Typography>
                                                        </TableCell>
                                                        <TableCell align="left" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">{formatCurrency((service.laborCost || 0) * service.labour)}</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                                {/* Show parts */}
                                                {service.details.map((detail, index) => (
                                                    <TableRow key={detail.no} sx={styles.expandedBodyRow}>
                                                        <TableCell align="right" sx={styles.expandedBodyCellLeft}>
                                                            <Typography variant="body2" mr={'8px'}>
                                                                {service.pricingType === PRICING_TYPES.LABOUR && (service.labour > 0 || service.laborCost > 0)
                                                                    ? index + 2
                                                                    : index + 1}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="left" sx={styles.expandedBodyCellLeft}>
                                                            <Typography variant="body2">{detail.partDetails}</Typography>
                                                            {detail.partNumber && (
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    {detail.partNumber}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">{detail.quantity}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">{detail.cost === '-' ? '-' : formatCurrency(detail.cost)}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">{formatCurrency(detail.price)}</Typography>
                                                        </TableCell>
                                                        <TableCell align="left" sx={styles.expandedBodyCell}>
                                                            <Typography variant="body2">{formatCurrency(detail.amount)}</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </>
                                    )}
                                </Table>
                            </TableContainer>
                        </Box>
                    </TableCell>
                </TableRow>
            )}

            {/* Vehicle Makes Modal */}
            <VehicleMakesModal open={vehicleMakesModalOpen} onClose={handleVehicleMakesModalClose} vehicleMakes={selectedVehicleMakes} />
        </>
    );
};

export default ServiceItem;
